import React, { useState, useEffect, useCallback } from 'react';
import { communityAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, X, Save } from 'lucide-react';

const iconOptions = ['Home', 'Utensils', 'Heart', 'Microscope', 'Smile', 'Dna', 'Building', 'Megaphone', 'Users'];

const AdminCommunity = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState({
    name: '', tagline: '', description: '', impact: '', type: '', icon: 'Heart', image: '', link: '', blog_slug: ''
  });
  const [error, setError] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await communityAPI.getAll();
      setProjects(data || []);
    } catch { setError('Failed to load community projects'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const resetForm = () => {
    setForm({ name: '', tagline: '', description: '', impact: '', type: '', icon: 'Heart', image: '', link: '', blog_slug: '' });
    setEditingProject(null);
    setShowForm(false);
  };

  const handleEdit = (project) => {
    setForm({
      name: project.name, tagline: project.tagline || '', description: project.description,
      impact: project.impact || '', type: project.type || '', icon: project.icon || 'Heart',
      image: project.image || '', link: project.link || '', blog_slug: project.blog_slug || '',
    });
    setEditingProject(project);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, image: form.image || null, link: form.link || null, blog_slug: form.blog_slug || null };
    try {
      if (editingProject) {
        await communityAPI.update(editingProject.id, payload);
      } else {
        await communityAPI.create(payload);
      }
      resetForm();
      fetchProjects();
    } catch (err) { alert('Error saving project'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this community project?')) return;
    try {
      await communityAPI.delete(id);
      fetchProjects();
    } catch (err) { alert('Error deleting project'); }
  };

  return (
    <div data-testid="admin-community">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Community Projects</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-teal-700 hover:bg-teal-600" data-testid="add-community-button">
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      {showForm && (
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{editingProject ? 'Edit' : 'New'} Project</h2>
              <button onClick={resetForm} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Project Name *</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-slate-800 border-slate-700 text-white" required data-testid="comm-name-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Tagline</Label>
                  <Input value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} className="bg-slate-800 border-slate-700 text-white" data-testid="comm-tagline-input" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Description *</Label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full min-h-[100px] rounded-md border bg-slate-800 border-slate-700 text-white px-3 py-2 text-sm" required data-testid="comm-description-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Impact</Label>
                  <Input value={form.impact} onChange={e => setForm({...form, impact: e.target.value})} placeholder="e.g. 1000+ women screened" className="bg-slate-800 border-slate-700 text-white" data-testid="comm-impact-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Type</Label>
                  <Input value={form.type} onChange={e => setForm({...form, type: e.target.value})} placeholder="e.g. Screening, NGO, Research" className="bg-slate-800 border-slate-700 text-white" data-testid="comm-type-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Icon</Label>
                  <select value={form.icon} onChange={e => setForm({...form, icon: e.target.value})} className="w-full h-10 rounded-md border bg-slate-800 border-slate-700 text-white px-3 text-sm" data-testid="comm-icon-select">
                    {iconOptions.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Website Link (optional)</Label>
                  <Input value={form.link} onChange={e => setForm({...form, link: e.target.value})} placeholder="https://..." className="bg-slate-800 border-slate-700 text-white" data-testid="comm-link-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Related Blog Post Slug (optional)</Label>
                  <Input value={form.blog_slug} onChange={e => setForm({...form, blog_slug: e.target.value})} placeholder="e.g. project-smart-pink-update" className="bg-slate-800 border-slate-700 text-white" data-testid="comm-blog-slug-input" />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-teal-700 hover:bg-teal-600" data-testid="comm-save-button">
                  <Save className="w-4 h-4 mr-2" /> {editingProject ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="ghost" onClick={resetForm} className="text-slate-400">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-slate-400">Loading...</div>
      ) : projects.length === 0 ? (
        <div className="text-center text-slate-400 py-12">No community projects yet.</div>
      ) : (
        <div className="space-y-3">
          {projects.map(project => (
            <Card key={project.id} className="bg-slate-900 border-slate-800" data-testid={`comm-item-${project.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium truncate">{project.name}</h3>
                    {project.type && <Badge variant="outline" className="text-xs border-slate-700 text-rose-400">{project.type}</Badge>}
                  </div>
                  <p className="text-slate-500 text-sm truncate">{project.tagline || project.description}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(project)} className="text-slate-400 hover:text-white">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(project.id)} className="text-slate-400 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCommunity;
